/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


/**
 * @author Yann Massard (yamass@gmail.com)
 */

import * as Mustache from "mustache";
import * as moment from "moment-timezone";
import {UiInstantTimeFieldConfig, UiInstantTimeFieldSubCommandHandler, UiInstantTimeFieldSubEventSource} from "../../../generated/UiInstantTimeFieldConfig";
import {TeamAppsUiContext} from "../../TeamAppsUiContext";
import {TeamAppsUiComponentRegistry} from "../../TeamAppsUiComponentRegistry";
import {AbstractUiTimeField} from "./AbstractUiTimeField";
import Moment = moment.Moment;
import {convertJavaDateTimeFormatToMomentDateTimeFormat} from "../../Common";

export class UiInstantTimeField extends AbstractUiTimeField<UiInstantTimeFieldConfig, number> implements UiInstantTimeFieldSubEventSource, UiInstantTimeFieldSubCommandHandler {

	private timeZoneId: string;

	protected initialize(config: UiInstantTimeFieldConfig, targetElement: Element, context: TeamAppsUiContext) {
		super.initialize(config, targetElement, context);
		this.timeZoneId = config.timeZoneId;
		this.getMainDomElement().addClass("UiInstantTimeField");
	}

	isValidData(v: number): boolean {
		return v == null || typeof v === "number";
	}

	protected displayCommittedValue(): void {
		let uiValue = this.getCommittedValue();
		if (uiValue) {
			let newMoment: Moment = moment.tz(uiValue, this.getTimeZoneId());
			this.trivialComboBox.setSelectedEntry(UiInstantTimeField.createTimeComboBoxEntry(newMoment.hour(), newMoment.minute(), this.getTimeFormat()), true);
		} else {
			this.trivialComboBox.setSelectedEntry(null, true);
		}
	}

	public getTransientValue(): number {
		let selectedEntry = this.trivialComboBox.getSelectedEntry();
		if (selectedEntry) {
			let selectedMoment = moment.tz({
				hour: selectedEntry.hour,
				minute: selectedEntry.minute
			}, this.getTimeZoneId());
			return selectedMoment.valueOf();
		} else {
			return null;
		}
	}

	private getTimeZoneId() {
		return this.timeZoneId || this._context.config.timeZoneId;
	}

	public static getReadOnlyHtml(config: UiInstantTimeFieldConfig, value: number, context: TeamAppsUiContext, drawBorder: boolean): string {
		if (value != null && value != null) {
			let mom = moment.tz(value, config.timeZoneId || context.config.timeZoneId);
			return `<div class="static-readonly-UiInstantTimeField ${drawBorder ? "bordered" : ""}">` + Mustache.render(UiInstantTimeField.comboBoxTemplate, UiInstantTimeField.createTimeComboBoxEntryFromMoment(mom, config.timeFormat || context.config.timeFormat)) + '</div>';
		} else {
			return "";
		}
	}

	setTimeZoneId(timeZoneId: string): void {
		this.timeZoneId = timeZoneId;
		this.displayCommittedValue();
	}

	public valuesChanged(v1: number, v2: number): boolean {
		return v1 !== v2;
	}
}

TeamAppsUiComponentRegistry.registerFieldClass("UiInstantTimeField", UiInstantTimeField);